import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import {
  Target, Scale, TrendingUp, Users, AlertTriangle, CheckCircle, Info,
  Loader, BarChart3, FileText, ChevronRight, ChevronDown, ArrowRight,
  Layers, Globe, Factory, Leaf, Shield, Building2, Heart, Briefcase,
  Lock, Crown, Sparkles
} from 'lucide-react';
import { PremiumGate } from '../components/PremiumGate';

// Topic Card Component
const TopicCard = ({ topic, topicId, onSelect, isSelected }) => {
  return (
    <div
      onClick={() => onSelect(topicId)}
      className={`p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md ${
        isSelected 
          ? 'border-indigo-500 bg-indigo-50' 
          : 'border-slate-200 bg-white hover:border-indigo-300'
      }`}
      data-testid={`topic-${topicId}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
          {topic.standard}
        </span>
        {isSelected && <CheckCircle size={16} className="text-indigo-600" />}
      </div>
      <h4 className="font-bold text-slate-900 mb-1">{topic.name}</h4>
      <div className="flex flex-wrap gap-1 mt-2">
        {topic.subtopics?.slice(0, 2).map((st, idx) => (
          <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
            {st}
          </span>
        ))}
        {topic.subtopics?.length > 2 && (
          <span className="text-xs text-slate-400">+{topic.subtopics.length - 2}</span>
        )}
      </div>
    </div>
  );
};

// Assessment Form Component
const AssessmentForm = ({ selectedTopic, topics, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    impact_type: 'actual',
    impact_scale: 3,
    impact_scope: 3,
    impact_irremediability: 3,
    impact_likelihood: 'possible',
    impact_timeframe: 'medium_term',
    risk_magnitude: 3,
    risk_likelihood: 'possible',
    opportunity_magnitude: 3,
    opportunity_likelihood: 'possible',
    description: '',
    framework: 'CSRD'
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...formData, topic_id: selectedTopic });
  };

  const topic = topics[selectedTopic];

  if (!selectedTopic) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Target size={48} className="mx-auto mb-4 opacity-50" />
        <p>Select a topic from the left to begin assessment</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
        <h3 className="font-bold text-indigo-900">{topic?.name}</h3>
        <p className="text-sm text-indigo-700">{topic?.standard}</p>
      </div>

      {/* Impact Materiality Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <AlertTriangle size={18} className="text-red-500" />
          Impact Materiality
        </h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Impact Type</label>
            <select 
              value={formData.impact_type}
              onChange={(e) => handleChange('impact_type', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="actual">Actual Impact</option>
              <option value="potential">Potential Impact</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Timeframe</label>
            <select 
              value={formData.impact_timeframe}
              onChange={(e) => handleChange('impact_timeframe', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="short_term">Short Term (&lt; 1 year)</option>
              <option value="medium_term">Medium Term (1-5 years)</option>
              <option value="long_term">Long Term (&gt; 5 years)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Scale (1-5)</label>
            <input 
              type="range" min="1" max="5" 
              value={formData.impact_scale}
              onChange={(e) => handleChange('impact_scale', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm font-bold text-indigo-600">{formData.impact_scale}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Scope (1-5)</label>
            <input 
              type="range" min="1" max="5" 
              value={formData.impact_scope}
              onChange={(e) => handleChange('impact_scope', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm font-bold text-indigo-600">{formData.impact_scope}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Irremediability (1-5)</label>
            <input 
              type="range" min="1" max="5" 
              value={formData.impact_irremediability}
              onChange={(e) => handleChange('impact_irremediability', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-sm font-bold text-indigo-600">{formData.impact_irremediability}</div>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Likelihood</label>
          <select 
            value={formData.impact_likelihood}
            onChange={(e) => handleChange('impact_likelihood', e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="rare">Rare (&lt; 5%)</option>
            <option value="unlikely">Unlikely (5-20%)</option>
            <option value="possible">Possible (20-50%)</option>
            <option value="likely">Likely (50-80%)</option>
            <option value="almost_certain">Almost Certain (&gt; 80%)</option>
          </select>
        </div>
      </div>

      {/* Financial Materiality Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
          <TrendingUp size={18} className="text-emerald-500" />
          Financial Materiality
        </h4>
        
        <div className="grid grid-cols-2 gap-6">
          {/* Risks */}
          <div className="bg-red-50 rounded-lg p-4">
            <h5 className="font-medium text-red-800 mb-3">Risks</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-red-700 mb-1">Magnitude (1-5)</label>
                <input 
                  type="range" min="1" max="5" 
                  value={formData.risk_magnitude}
                  onChange={(e) => handleChange('risk_magnitude', parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="text-center text-sm font-bold text-red-600">{formData.risk_magnitude}</div>
              </div>
              <div>
                <label className="block text-sm text-red-700 mb-1">Likelihood</label>
                <select 
                  value={formData.risk_likelihood}
                  onChange={(e) => handleChange('risk_likelihood', e.target.value)}
                  className="w-full px-2 py-1 border border-red-200 rounded text-sm"
                >
                  <option value="rare">Rare</option>
                  <option value="unlikely">Unlikely</option>
                  <option value="possible">Possible</option>
                  <option value="likely">Likely</option>
                  <option value="almost_certain">Almost Certain</option>
                </select>
              </div>
            </div>
          </div>

          {/* Opportunities */}
          <div className="bg-emerald-50 rounded-lg p-4">
            <h5 className="font-medium text-emerald-800 mb-3">Opportunities</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-emerald-700 mb-1">Magnitude (1-5)</label>
                <input 
                  type="range" min="1" max="5" 
                  value={formData.opportunity_magnitude}
                  onChange={(e) => handleChange('opportunity_magnitude', parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="text-center text-sm font-bold text-emerald-600">{formData.opportunity_magnitude}</div>
              </div>
              <div>
                <label className="block text-sm text-emerald-700 mb-1">Likelihood</label>
                <select 
                  value={formData.opportunity_likelihood}
                  onChange={(e) => handleChange('opportunity_likelihood', e.target.value)}
                  className="w-full px-2 py-1 border border-emerald-200 rounded text-sm"
                >
                  <option value="rare">Rare</option>
                  <option value="unlikely">Unlikely</option>
                  <option value="possible">Possible</option>
                  <option value="likely">Likely</option>
                  <option value="almost_certain">Almost Certain</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Assessment Notes</label>
        <textarea 
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Describe the rationale for your assessment..."
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 h-24"
        />
      </div>

      <button 
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="submit-assessment-btn"
      >
        {loading ? <Loader className="animate-spin" size={18} /> : <CheckCircle size={18} />}
        {loading ? 'Assessing...' : 'Submit Assessment'}
      </button>
    </form>
  );
};

// Materiality Matrix Visualization
const MaterialityMatrix = ({ matrixData }) => {
  if (!matrixData || !matrixData.matrix_data) {
    return (
      <div className="text-center py-8 text-slate-500">
        <BarChart3 size={48} className="mx-auto mb-4 opacity-50" />
        <p>No assessments yet. Complete assessments to see your materiality matrix.</p>
      </div>
    );
  }

  const { matrix_data, quadrant_summary } = matrixData;

  return (
    <div className="space-y-6">
      {/* Matrix Grid */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h4 className="font-bold text-slate-900 mb-4">Double Materiality Matrix</h4>
        
        <div className="relative h-80 border-2 border-slate-300 rounded-lg">
          {/* Y-axis label */}
          <div className="absolute -left-8 top-1/2 -translate-y-1/2 -rotate-90 text-sm font-medium text-slate-600">
            Financial Materiality →
          </div>
          
          {/* X-axis label */}
          <div className="absolute bottom-[-30px] left-1/2 -translate-x-1/2 text-sm font-medium text-slate-600">
            Impact Materiality →
          </div>
          
          {/* Quadrants */}
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
            <div className="bg-amber-50 border-r border-b border-slate-200 flex items-center justify-center">
              <span className="text-xs text-amber-600 font-medium">Financial Only</span>
            </div>
            <div className="bg-red-50 border-b border-slate-200 flex items-center justify-center">
              <span className="text-xs text-red-600 font-medium">Double Material</span>
            </div>
            <div className="bg-slate-50 border-r border-slate-200 flex items-center justify-center">
              <span className="text-xs text-slate-400 font-medium">Not Material</span>
            </div>
            <div className="bg-blue-50 flex items-center justify-center">
              <span className="text-xs text-blue-600 font-medium">Impact Only</span>
            </div>
          </div>

          {/* Plot points */}
          {matrix_data.map((item, idx) => (
            <div
              key={idx}
              className={`absolute w-4 h-4 rounded-full transform -translate-x-1/2 -translate-y-1/2 cursor-pointer
                ${item.is_material ? 'bg-red-500' : 'bg-slate-400'}`}
              style={{
                left: `${Math.min(95, Math.max(5, item.impact_score * 20))}%`,
                bottom: `${Math.min(95, Math.max(5, item.financial_score * 20))}%`
              }}
              title={`${item.topic_name}: Impact ${item.impact_score.toFixed(1)}, Financial ${item.financial_score.toFixed(1)}`}
            />
          ))}
        </div>
      </div>

      {/* Quadrant Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-red-50 rounded-xl p-4 border border-red-200">
          <h5 className="font-bold text-red-800 mb-2">Double Material</h5>
          <p className="text-2xl font-bold text-red-600">{quadrant_summary?.high_impact_high_financial?.length || 0}</p>
          <p className="text-xs text-red-600">High priority topics</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <h5 className="font-bold text-blue-800 mb-2">Impact Only</h5>
          <p className="text-2xl font-bold text-blue-600">{quadrant_summary?.high_impact_low_financial?.length || 0}</p>
          <p className="text-xs text-blue-600">Stakeholder focus</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
          <h5 className="font-bold text-amber-800 mb-2">Financial Only</h5>
          <p className="text-2xl font-bold text-amber-600">{quadrant_summary?.low_impact_high_financial?.length || 0}</p>
          <p className="text-xs text-amber-600">Business risk focus</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <h5 className="font-bold text-slate-700 mb-2">Not Material</h5>
          <p className="text-2xl font-bold text-slate-500">{quadrant_summary?.low_impact_low_financial?.length || 0}</p>
          <p className="text-xs text-slate-500">Monitor only</p>
        </div>
      </div>
    </div>
  );
};

// Main IRO Assessment Page
const IROAssessment = () => {
  const [topics, setTopics] = useState({});
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('assess');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [topicsData, matrix] = await Promise.all([
      apiFetch(`${API}/iro/topics`),
      apiFetch(`${API}/iro/matrix`)
    ]);

    if (topicsData?._error || matrix?._error) {
      console.error('Failed to fetch:', topicsData?.message || matrix?.message);
      setLoading(false);
      return;
    }

    setTopics(topicsData.topics || {});
    setMatrixData(matrix);
    setLoading(false);
  };

  const handleSubmitAssessment = async (formData) => {
    setSubmitting(true);
    const data = await apiFetch(`${API}/iro/assess`, {
      method: 'POST',
      body: formData
    });
    if (data?._error) {
      console.error('Failed to submit:', data.message);
      setSubmitting(false);
      return;
    }
    setAssessmentResult(data);
    // Refresh matrix
    const matrix = await apiFetch(`${API}/iro/matrix`);
    if (matrix?._error) {
      console.error('Failed to submit:', matrix.message);
    } else {
      setMatrixData(matrix);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="iro-assessment-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-3xl font-bold">IRO Assessment Matrix</h1>
              <span className="px-2 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full flex items-center gap-1">
                <Crown size={12} /> PREMIUM
              </span>
            </div>
            <p className="text-indigo-200">
              Impact, Risk, and Opportunity assessment for ESRS double materiality compliance
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-indigo-200">Topics Assessed</p>
            <p className="text-3xl font-bold">{matrixData?.total_topics_assessed || 0} / {Object.keys(topics).length}</p>
          </div>
        </div>
      </div>

      <PremiumGate featureKey="iro-assessment">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          <button
            onClick={() => setActiveTab('assess')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === 'assess' 
                ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="tab-assess"
          >
            <Target className="inline mr-2" size={16} />
            Assess Topics
          </button>
          <button
            onClick={() => setActiveTab('matrix')}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors ${
              activeTab === 'matrix' 
                ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="tab-matrix"
          >
            <BarChart3 className="inline mr-2" size={16} />
            Materiality Matrix
          </button>
        </div>

        {activeTab === 'assess' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Topics List */}
            <div className="lg:col-span-1 space-y-3">
              <h3 className="font-bold text-slate-900">ESRS Topics</h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                {Object.entries(topics).map(([topicId, topic]) => (
                  <TopicCard
                    key={topicId}
                    topic={topic}
                    topicId={topicId}
                    onSelect={setSelectedTopic}
                    isSelected={selectedTopic === topicId}
                  />
                ))}
              </div>
            </div>

            {/* Assessment Form */}
            <div className="lg:col-span-2">
              <AssessmentForm
                selectedTopic={selectedTopic}
                topics={topics}
                onSubmit={handleSubmitAssessment}
                loading={submitting}
              />

              {/* Result Display */}
              {assessmentResult && (
                <div className="mt-6 bg-emerald-50 rounded-xl border border-emerald-200 p-5">
                  <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                    <CheckCircle size={18} />
                    Assessment Complete
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-emerald-600">Impact Score</p>
                      <p className="text-2xl font-bold text-emerald-800">
                        {assessmentResult.impact_materiality?.final_score?.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-emerald-600">Financial Score</p>
                      <p className="text-2xl font-bold text-emerald-800">
                        {assessmentResult.financial_materiality?.combined_score?.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-emerald-600">Result</p>
                      <p className={`text-lg font-bold ${assessmentResult.is_material ? 'text-red-600' : 'text-slate-600'}`}>
                        {assessmentResult.is_material ? 'MATERIAL' : 'Not Material'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'matrix' && (
          <MaterialityMatrix matrixData={matrixData} />
        )}
      </PremiumGate>
    </div>
  );
};

export default IROAssessment;
